import { ulid } from "ulid"
import { createActionUtils, IAction, IActionJavaData, IActionWebConfig } from "@droid/project-v2-interface"
import { a11yActionNamespace } from "../a11y-namespace"
import { UI } from "./UI"
import { ManifestPermission } from "@droid/android"
import { RhinoInjectNames } from "@app/static"
import { Log } from "@fanfanlo"

const fileLog = new Log(false, '[IActionNodeAction]')
fileLog.pause = true
fileLog.childrenPaused = true

export const actionNodeActionType = `${a11yActionNamespace}node-action`
const type = actionNodeActionType
fileLog.log('[IActionNodeAction] type:', type)

/**
 * 节点操作类型枚举
 */
export enum ActionType {
    click = 'click',
    longClick = 'longClick',
    scroll = 'scroll',
    setText = 'setText',
    focus = 'focus'
}

/**
 * 连续点击配置
 */
export interface ContinuousClickConfig {
    /** 是否启用连续点击 */
    enableContinuousClick: boolean
    /** 基准间隔时间(毫秒) */
    baseInterval: number
    /** 间隔波动范围(毫秒,正负) */
    intervalVariation: number
}

/**
 * 点击配置
 */
export interface ClickConfig {
    /** 是否使用全局坐标点击(而不是node.click) */
    useGlobalClick: boolean
    /** 连续点击配置 */
    continuousClickConfig?: ContinuousClickConfig
}

/**
 * 滚动配置
 */
export interface ScrollConfig {
    /** 滚动方向 */
    direction: 'forward' | 'backward'
}

/**
 * 文本配置
 */
export interface TextConfig {
    /** 要设置的文本 */
    text: string
}

/**
 * NodeAction 的 Java 端数据结构
 */
export interface IActionNodeActionJavaData extends IActionJavaData {
    /** 操作类型 */
    actionType: ActionType
    /** 点击配置 (当actionType为click时使用) */
    clickConfig?: ClickConfig
    /** 滚动配置 (当actionType为scroll时使用) */
    scrollConfig?: ScrollConfig
    /** 文本配置 (当actionType为setText时使用) */
    textConfig?: TextConfig
}

/**
 * NodeAction 的完整数据结构
 */
export interface IActionNodeActionWebConfig extends IActionWebConfig {
    // Node Action 特有的 Web 配置
}

export interface IActionNodeActionData extends IAction<IActionNodeActionJavaData, IActionNodeActionWebConfig> {
    javaData: IActionNodeActionJavaData
}

/**
 * 创建默认的 NodeAction 数据
 */
function createDefaultData(): IActionNodeActionData {
    fileLog.log('[createDefaultData] 开始创建默认数据')

    const ulidValue = ulid()
    fileLog.log('[createDefaultData] 生成ulid:', ulidValue)

    const permissions = [
        {
            name: '辅助功能权限',
            value: ManifestPermission.BIND_ACCESSIBILITY_SERVICE,
            reason: '需要辅助功能来操作节点'
        }
    ]
    fileLog.log('[createDefaultData] 权限数量:', permissions.length)

    const injects: ([typeof RhinoInjectNames.a11yModel, string])[] = [
        [RhinoInjectNames.a11yModel, 'a11yModel']
    ]
    fileLog.log('[createDefaultData] 注入配置数量:', injects.length)

    const defaultData: IActionNodeActionData = {
        
        ulid: ulidValue,
        uuid: '',
        ownerUuid: '',
        currentVersion: 0,
        type,
        name: 'actionNodeAction',
        permissions,
        injects,
        javaData: {
            actionType: ActionType.click,
            clickConfig: {
                useGlobalClick: true,
                continuousClickConfig: {
                    enableContinuousClick: false,
                    baseInterval: 300,
                    intervalVariation: 50
                }
            }
        },
        createdAt: 0,
        updatedAt: 0,
        authorUuid: '',
    }
    fileLog.log('[createDefaultData] defaultData:', JSON.stringify(defaultData))
    return defaultData
}

/**
 * 初始化函数
 */
function init() {
    fileLog.log('[init] node-action 初始化')
}

/**
 * NodeAction 工具对象
 */
export const actionNodeActionUtils = createActionUtils(type, init, createDefaultData, UI)
